import React, { useState, useEffect,useRef } from "react";
import styled from "styled-components";
import { FiX } from "react-icons/fi";

const Modal = ({ isOpen, onClose, title, lastUpdated, children, onAccept }) => {
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const contentRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    setScrolledToBottom(false);
    setScrollProgress(0);
  }, [isOpen]);

  const handleScroll = () => {
    const el = contentRef.current;
    if (!el) return;

    const { scrollTop, scrollHeight, clientHeight } = el;
    const progress = (scrollTop / (scrollHeight - clientHeight)) * 100;
    setScrollProgress(progress);

    if (scrollTop + clientHeight >= scrollHeight - 5) {
      setScrolledToBottom(true);
    }
  };

  // 🔹 Dynamic color interpolation
  const getProgressColor = (progress) => {
    if (progress < 50) return "#888"; // grey
    if (progress < 90) return "#E55B41"; // orange
    return "#28a745"; // green
  };

  if (!isOpen) return null;

  return (
    <Overlay>
      <ModalContainer>
        <Header>
          <div>
            <h2>{title}</h2>
            {lastUpdated && <LastUpdated>Last updated: {lastUpdated}</LastUpdated>}
          </div>
          <CloseButton onClick={onClose}>
            <FiX size={20} />
          </CloseButton>
        </Header>

        {/* Progress bar */}
        <ProgressBar>
          <Progress
            style={{
              width: `${scrollProgress}%`,
              background: getProgressColor(scrollProgress),
            }}
          />
        </ProgressBar>

        <Content ref={contentRef} onScroll={handleScroll}>
          {children}
        </Content>

        <Footer>
          {/* <button onClick={onClose}>Cancel</button> */}
          <AcceptButton
            disabled={!scrolledToBottom}
            scrolledToBottom={scrolledToBottom}
            onClick={() => {
              onAccept?.();
              onClose();
            }}
          >
            I Agree
          </AcceptButton>
        </Footer>
      </ModalContainer>
    </Overlay>
  );
};

export default Modal;

// 🔹 Styled Components
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
`;

const ModalContainer = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(12px);
  border-radius: 16px;
  max-width: 600px;
  width: 90%;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Header = styled.div`
  position: sticky;
  top: 0;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(12px);
  padding: 1rem;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  z-index: 10;
`;

const LastUpdated = styled.div`
  font-size: 0.75rem;
  color: #666;
  margin-top: 0.25rem;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #fff;
  cursor: pointer;
  padding: 0.25rem;

  &:hover {
    color: #ff6b6b;
  }
`;

const Content = styled.div`
  padding: 1rem;
  overflow-y: auto;
  flex-grow: 1;
  font-size: 0.9rem;
  color: #ddd;
  line-height: 1.5;

  scrollbar-width: thin;
  scrollbar-color: #888 transparent;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 3px;
  }
`;

const Footer = styled.div`
  padding: 1rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;

  button {
    padding: 0.5rem 1rem;
    border-radius: 8px;
    border: none;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.3s ease;
  }

  button:first-child {
    background: #444;
    color: #fff;

    &:hover {
      background: #555;
    }
  }

  button:last-child {
    background: #ffd369;
    color: #222;

    &:disabled {
      background: #aaa;
      cursor: not-allowed;
    }

    &:hover:enabled {
      background: #ffae00;
    }
  }
`;

const ProgressBar = styled.div`
  position: sticky;
  top: 60px; /* Adjust to match header height */
  width: 100%;
  height: 4px;
  background: #eee;
  z-index: 9;
`;

const Progress = styled.div`
  height: 100%;
  transition: width 0.2s ease, background 0.3s ease;
`;


const AcceptButton = styled.button`
  padding: 0.6rem 1.2rem;
  border: none;
  border-radius: 4px;
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
  background: ${({ disabled }) => (disabled ? "#ccc" : "#28a745")};
  color: white;
  font-weight: 500;
  transition: all 0.4s ease;

  ${({ scrolledToBottom }) =>
    scrolledToBottom &&
    `
    animation: fadeInGlow 0.8s ease forwards;
    box-shadow: 0 0 10px rgba(40, 167, 69, 0.6);
  `}

  &:hover {
    background: ${({ disabled }) => (disabled ? "#ccc" : "#218838")};
  }

  @keyframes fadeInGlow {
    0% {
      transform: scale(0.95);
      opacity: 0.5;
      box-shadow: none;
    }
    100% {
      transform: scale(1);
      opacity: 1;
      box-shadow: 0 0 12px rgba(40, 167, 69, 0.8);
    }
  }
`;